import { useEffect, useState } from 'react';
import { authErrorMessage, useAuth } from '../auth/AuthProvider';
import { AppLink } from '../components/Nav';
import { Button, Spinner } from '../components/ui';
import { track } from '../lib/amplitude';
import { AuthFrame } from './AuthPages';

/**
 * Where the confirmation email lands.
 *
 * The code in the link is redeemed the moment the page opens; there is
 * nothing to fill in. Signed in, the account is refreshed so the app and the
 * API both see the address as confirmed straight away. Signed out, the
 * address is confirmed all the same, and the page says to sign in.
 */
export function VerifyEmailPage() {
  const { confirmEmail, user, loading } = useAuth();
  const [code] = useState(() => new URLSearchParams(window.location.search).get('oobCode') ?? '');
  const [state, setState] = useState<'working' | 'done' | 'failed'>('working');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading) return;
    if (!code) {
      setError('This link is missing its code. Use the button in the email, or ask for a new link from Settings.');
      setState('failed');
      return;
    }
    // The code does not stay in the address bar once spent.
    window.history.replaceState({}, '', '/verify-email');
    let live = true;
    confirmEmail(code)
      .then(() => {
        if (!live) return;
        track('Email Verified');
        setState('done');
      })
      .catch((cause: unknown) => {
        if (!live) return;
        setError(authErrorMessage(cause).replace('reset link', 'link'));
        setState('failed');
      });
    return () => {
      live = false;
    };
  }, [code, confirmEmail, loading]);

  if (state === 'working') {
    return (
      <div className="flex min-h-full items-center justify-center bg-slate-950">
        <Spinner size={22} className="text-chalk-faint" />
      </div>
    );
  }

  if (state === 'failed') {
    return (
      <AuthFrame
        title="That link is no good"
        subtitle="Confirmation links work once and expire after an hour."
        footer={
          <>
            Already confirmed?{' '}
            <AppLink to="/library" className="text-signal hover:underline">
              Go to your library
            </AppLink>
          </>
        }
      >
        <p className="text-body text-chalk-dim">{error}</p>
        <AppLink to={user ? '/settings' : '/login'} className="mt-5 block">
          <Button variant="primary" size="lg" className="w-full">
            {user ? 'Send a new link from Settings' : 'Sign in'}
          </Button>
        </AppLink>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame
      title="Your email address is confirmed"
      subtitle={user ? 'Everything is open. Nothing else to do here.' : 'Sign in and carry on.'}
      footer={<span>You can close this tab.</span>}
    >
      <AppLink to={user ? '/library' : '/login'} className="block">
        <Button variant="primary" size="lg" className="w-full">
          {user ? 'Go to your library' : 'Sign in'}
        </Button>
      </AppLink>
    </AuthFrame>
  );
}
